import { commandBarOpenAtom, getHotkey, toast, useAdminContext } from "@aix/ui"
import { useNavigate } from "@tanstack/react-router"
import { useAtom } from "jotai"
import { last } from "lodash-es"
import { useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import { api } from "~/utils/api"
import { keyboardShortcut, navigationTriggerKey } from "~/utils/keyboardShortcuts"

import { demoModeAtom, superuserPanelOpenAtom } from "../context/jotai"
import { useTheme } from "../lib/useTheme"

export const useGlobalShortcuts = () => {
  const { isAdmin } = useAdminContext()
  const navigate = useNavigate()

  const { theme, toggleTheme } = useTheme()
  const ctx = api.useUtils()
  const user = ctx.users.getUserWithTeam.getData()

  const [navActivated, setNavActivated] = useState(false)
  const [, setCommandBarOpen] = useAtom(commandBarOpenAtom)
  const [, setSuperuserPanelOpen] = useAtom(superuserPanelOpenAtom)
  const [demoMode, setDemoMode] = useAtom(demoModeAtom)

  // global.toggleDarkMode
  useHotkeys(
    getHotkey(keyboardShortcut.global.darkMode.keys),
    () => {
      if (theme === "light") {
        toggleTheme()
      } else {
        toggleTheme()
      }
    },
    {
      preventDefault: true,
      enableOnFormTags: true,
      enableOnContentEditable: true,
    },
  )

  // global.demoMode — Cmd+Shift+D toggles demo mode (hides all admin UI for screen recordings)
  // Only available for admin users — non-admins can't toggle
  useHotkeys(
    getHotkey(keyboardShortcut.global.demoMode.keys),
    () => {
      if (!isAdmin && !demoMode) return // non-admin can't enter demo mode (but can exit if stuck)
      const next = !demoMode
      setDemoMode(next)
      toast.success(next ? "Demo mode ON — admin UI hidden" : "Demo mode OFF — admin UI visible")
    },
    {
      preventDefault: true,
      enableOnFormTags: true,
      enableOnContentEditable: true,
    },
    [demoMode, isAdmin],
  )

  useHotkeys(
    getHotkey(keyboardShortcut.global.search.keys),
    () => {
      setCommandBarOpen((open) => !open)
    },
    {
      preventDefault: true,
      enableOnFormTags: true,
      enableOnContentEditable: true,
    },
  )

  useHotkeys(
    getHotkey(keyboardShortcut.global.prompt.keys),
    () => {
      // uses this instead of isAdminUserFE because it needs to have the off switch as well
      if (user?.internalMisc?.isSuper) {
        setSuperuserPanelOpen((open) => !open)
      }
    },
    {
      preventDefault: true,
      enableOnFormTags: true,
      enableOnContentEditable: true,
    },
    [user],
  )

  // NAVIGATION
  useHotkeys(
    navigationTriggerKey,
    () => {
      setNavActivated(true)
      setTimeout(() => setNavActivated(false), 750)
    },
    { preventDefault: true },
  )

  // navigation.navigateToSubscription
  useHotkeys(
    last(keyboardShortcut.navigation.billing.keys)!,
    () => {
      void navigate({ to: "/billing" })
      setNavActivated(false)
    },
    { enabled: navActivated, preventDefault: true },
    [navActivated],
  )
}
